import React, { forwardRef, useId, useState } from "react";
import tw from "twin.macro";
import {ReactComponent as EyeOutline} from "app/assets/icons/eye.svg";
import {ReactComponent as EyeSlashOutline} from "app/assets/icons/eye-slash.svg";

export interface TextFieldProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  badge?: string;
  label: string;
  error?: boolean;
  hintText?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  containerClassName?: string;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      type,
      label,
      error,
      hintText,
      badge,
      className,
      id: __id,
      leadingIcon,
      trailingIcon,
      disabled,
      readOnly,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const _id = useId();
    const id = __id ?? _id;

    const [obscureText, setObscureText] = useState(type === "password");

    const hasValue = props.value && props.value.toString().trim().length > 0;

    return (
      <div tw="flex flex-col gap-s2" className={containerClassName}>
        <div
          css={[
            tw`border border-border-mid rounded-m h-[56px] py-s2 px-s3 bg-input-surface flex items-center gap-s2 transition-colors duration-200 focus-within:border-[length:var(--genesis-border-width-thick)]  focus-within:border-border-higher`,
            error &&
            tw`focus-within:border-border-error border-border-error border-[length:var(--genesis-border-width-thick)]`,
            disabled && tw`pointer-events-none opacity-50`,
          ]}
          className={className}
          onClick={(e) => {
            e.currentTarget.querySelector("input")?.focus();
          }}
        >
          {leadingIcon}

          <div tw="relative min-h-[40px] flex items-end grow">
            <input
              type={obscureText ? "password" : "text"}
              ref={ref}
              id={id}
              css={[
                tw`mt-s1 body-large text-text-high w-full outline-none transition-[colors,opacity] duration-200`,
                tw`placeholder-shown:opacity-0 placeholder-shown:focus:opacity-100`,
                error ? tw`border-border-error` : tw`border-border-mid`,
                (readOnly || disabled) && tw`pointer-events-none`,
                badge && tw`pr-s18`
              ]}
              {...(error ? { "aria-invalid": "true" } : {})}
              placeholder={props.placeholder ?? " "}
              className="peer"
              {...props}
            />

            {badge && (
              <div tw="absolute top-1/2 -translate-y-1/2 right-0 py-s1 px-s2 bg-containment-tertiary rounded-lg">
                <span tw="label-large text-text-low">{badge}</span>
              </div>
            )}

            <label
              htmlFor={id}
              css={[
                tw`absolute origin-left left-0 top-1/2 -translate-y-1/2 scale-100 transition-all duration-200 pointer-events-none body-large text-input-on-primary-variant`,
                tw`peer-focus:-top-s1 peer-focus:translate-y-0 peer-focus:text-[12px]`,
                hasValue && tw`-top-s1 translate-y-0 text-[12px]`
              ]}
            >
              {label}
            </label>
          </div>

          {type === "password" && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                setObscureText((o) => !o);
              }}
            >
              {obscureText ? (
                <EyeOutline tw="w-[20px] h-[20px] self-center text-text-mid" />
              ) : (
                <EyeSlashOutline tw="w-[20px] h-[20px] self-center text-text-mid" />
              )}
            </button>
          )}

          {trailingIcon}
        </div>

        {
          hintText && (
            <div
              css={[
                tw`body-small text-text-mid`,
                error ? tw`text-input-error` : tw`text-text-mid`
              ]}
            >
              {hintText}
            </div>
          )
        }
      </div >
    );
  }
);

TextField.displayName = "TextField";
